import {
    Paper,
    Typography,
    Stack,
    Divider,
    Box,
    Chip,
    CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import CarrierLogo from "./CarrierLogo";

import { fmtTime as fmtTimeShared } from "../utils/format";

const fmtTime = (raw) => fmtTimeShared(raw, "--:--");

function SummaryRow({ icon, label, children, inline }) {
    if (inline) {
        return (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={0.8} alignItems="center">
                    <Icon icon={icon} width={16} color="#919EAB" />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", fontWeight: 500 }}>
                        {label}
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {children}
                </Typography>
            </Stack>
        );
    }
    return (
        <Stack spacing={0.3}>
            <Stack direction="row" spacing={0.8} alignItems="center">
                <Icon icon={icon} width={16} color="#919EAB" />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", fontWeight: 500 }}>
                    {label}
                </Typography>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 600, pl: 3 }}>
                {children}
            </Typography>
        </Stack>
    );
}

export default function BookingSummary({
    trip,
    passengers,
    selectedSeats,
    totalPrice,
    isRoundTrip = false,
    loading = false,
}) {
    const { t } = useTranslation();
    if (!trip) return null;

    const depStation = trip.departureStationName || trip.fromStation || "";
    const arrStation = trip.arrivalStationName || trip.toStation || "";
    const operator = trip.operatorName || trip.carrierName || "";
    const operatorId = trip.operatorId || trip.carrierId || null;

    return (
        <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
            {/* Header */}
            <Box sx={{ bgcolor: "primary.main", px: 2.5, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ color: "white", fontWeight: 700 }}>
                    {t("booking.summary")}
                </Typography>
            </Box>

            <Stack spacing={1.5} sx={{ p: 2.5 }}>
                {/* Ruta */}
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {fmtTime(trip.departureTime)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {depStation}
                        </Typography>
                    </Stack>
                    <Box sx={{ ml: "3px", borderLeft: "2px dashed", borderColor: "grey.300", height: 12 }} />
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {fmtTime(trip.arrivalTime)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {arrStation}
                        </Typography>
                    </Stack>
                </Box>

                <Divider />

                {/* Prevoznik sa logom */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <CarrierLogo operatorId={operatorId} size={48} />
                    <Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                            {t("booking.operator")}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {operator || "N/A"}
                        </Typography>
                    </Stack>
                </Stack>

                <SummaryRow icon="mdi:account-group" label={t("booking.passengersCount")} inline>
                    {passengers}
                </SummaryRow>

                {selectedSeats.length > 0 && (
                    <SummaryRow icon="mdi:seat" label={t("booking.seats")}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {selectedSeats.map((seat) => (
                                <Chip
                                    key={seat.seatNumber}
                                    label={seat.seatNumber}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: "0.75rem" }}
                                />
                            ))}
                        </Stack>
                    </SummaryRow>
                )}

                <Divider />

                {/* Oznaka da je povratna karta (priceReturnTicket je puna cena povratne) */}
                {isRoundTrip && (
                    <Stack direction="row" justifyContent="flex-end">
                        <Chip
                            icon={<Icon icon="mdi:swap-horizontal" width={14} />}
                            label={t("results.returnTicket")}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 22,
                                bgcolor: "#1a1a2e",
                                color: "white",
                                "& .MuiChip-icon": { color: "white" },
                            }}
                        />
                    </Stack>
                )}

                {/* Ukupno */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        {t("booking.total")}
                    </Typography>
                    {loading ? (
                        <CircularProgress size={22} />
                    ) : (
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "primary.main" }}>
                            {totalPrice
                                ? `${Number(totalPrice).toLocaleString("sr-Latn", { minimumFractionDigits: 2 })} RSD`
                                : "N/A"}
                        </Typography>
                    )}
                </Stack>
            </Stack>
        </Paper>
    );
}
